/**
 * OfferSummary - The investor's offer as a gray card: title, deal information,
 * the negotiation history while the offer is engaged, and the caller's actions.
 *
 * Presentational only. It reads no session, router or toast, so the signed-in
 * property page and the partner link page both render it and each supplies its
 * own actions (withdraw, counter offer, contract upload).
 */

import type { ReactNode } from 'react'
import clsx from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { Text } from '@/common/components/text'
import { Icon } from '@/common/components/icon'
import { Tooltip } from '@/common/components/tooltip'
import { Badge } from '@/common/components/badge'
import type { ClosingTimeline, OfferStatus } from '../../api/offers'
import { NegotiationTimeline } from '../NegotiationTimeline'
import type { NegotiationTimelineEntry } from '../NegotiationTimeline/NegotiationTimeline'
import * as styles from './OfferSummary.css'

// Map offer status to badge color
const STATUS_BADGE_COLOR: Record<OfferStatus, 'caution' | 'success' | 'error' | 'neutral' | 'info'> = {
  pending: 'caution',
  engaged: 'info',
  accepted: 'success',
  rejected: 'error',
  withdrawn: 'neutral',
}

/** The offer fields the summary reads. */
export interface OfferSummaryOffer {
  offer_price: number
  status: OfferStatus
  created_at: string
  earnest_money_deposit?: number | null
  closing_timeline?: ClosingTimeline | null
  notes?: string | null
  private_notes?: string | null
}

export interface OfferSummaryProps {
  offer: OfferSummaryOffer
  /** Negotiation entries, oldest first. Shown only while the offer is engaged. */
  negotiations?: readonly NegotiationTimelineEntry[]
  /** Rendered as direct children of the card, below the summary. */
  actions?: ReactNode
  className?: string
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatRelativeTime(dateString: string): string {
  return formatDistanceToNow(new Date(dateString), { addSuffix: true })
}

function formatClosingTimeline(timeline: ClosingTimeline): string {
  switch (timeline) {
    case '10_days':
      return '10 days'
    case '14_days':
      return '14 days'
    case '30_days':
      return '30 days'
    case 'flexible':
      return 'Flexible'
    default:
      return timeline
  }
}

export function OfferSummary({ offer, negotiations, actions, className }: OfferSummaryProps) {
  const {
    offer_price,
    status,
    earnest_money_deposit,
    closing_timeline,
    notes,
    private_notes,
    created_at,
  } = offer

  return (
    <div className={clsx(styles.card, className)}>
      {/* Title */}
      <Text variant="bodyLg" weight="bold">Your offer</Text>

      {/* Deal info section (white box) */}
      <div className={styles.dealInfoSection}>
        {/* Header section */}
        <div className={styles.headerSection}>
          <div className={styles.offerHeader}>
            <Text variant="bodyLg" weight="bold">{formatCurrency(offer_price)}</Text>
            <Badge color={STATUS_BADGE_COLOR[status]} size="sm" style={{ textTransform: 'capitalize' }}>
              {status}
            </Badge>
          </div>
          <Text variant="bodyXs" color="muted">Submitted {formatRelativeTime(created_at)}</Text>
        </div>

        {/* Separator */}
        <div className={styles.separator} />

        {closing_timeline && (
          <div className={styles.dealInfoItem}>
            <Text variant="bodyXs" color="muted" className={styles.dealInfoLabel}>
              CLOSING TIMELINE
            </Text>
            <Text variant="bodySm">{formatClosingTimeline(closing_timeline)}</Text>
          </div>
        )}
        {earnest_money_deposit != null && (
          <div className={styles.dealInfoItem}>
            <Text variant="bodyXs" color="muted" className={styles.dealInfoLabel}>
              EARNEST MONEY DEPOSIT
            </Text>
            <Text variant="bodySm">{earnest_money_deposit}%</Text>
          </div>
        )}
        {notes && (
          <div className={styles.dealInfoItem}>
            <Text variant="bodyXs" color="muted" className={styles.dealInfoLabel}>
              NOTES
            </Text>
            <Text variant="bodySm">{notes}</Text>
          </div>
        )}
        {private_notes && (
          <div className={styles.dealInfoItem}>
            <Text variant="bodyXs" color="muted" className={styles.dealInfoLabel}>
              PRIVATE NOTES
              <Tooltip content="Not shared with the agent" delayDuration={0}>
                <span className={styles.visibilityIcon}>
                  <Icon name="visibility_off" size={14} />
                </span>
              </Tooltip>
            </Text>
            <Text variant="bodySm">{private_notes}</Text>
          </div>
        )}
      </div>

      {/* Negotiation timeline for engaged offers */}
      {status === 'engaged' && negotiations && negotiations.length > 0 && (
        <div>
          <Text variant="bodySm" weight="bold" style={{ marginBottom: 8 }}>Negotiation History</Text>
          <NegotiationTimeline negotiations={negotiations} />
        </div>
      )}

      {actions}
    </div>
  )
}
